import { Button } from '@/Components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/Components/ui/dialog';
import { Label } from '@/Components/ui/label';
import { Separator } from '@/Components/ui/separator';
import { Switch } from '@/Components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Components/ui/tabs';
import { useToast } from '@/Hooks/use-toast';
import { ShieldCheck } from 'lucide-react';
import React, { useEffect, useState } from 'react';

export interface CookiePreferences {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
}

interface CookieConsentProps {
    privacyPolicyUrl: string;
    cookiePolicyUrl?: string;
    consentVersion: string;
}

const CookieConsent: React.FC<CookieConsentProps> = ({
    privacyPolicyUrl,
    cookiePolicyUrl,
    consentVersion,
}) => {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
    });
    const [activeTab, setActiveTab] = useState<string>('simple');

    useEffect(() => {
        // Check if consent has been given
        const cookieConsent = localStorage.getItem('cookieConsent');

        // If no consent, show the dialog
        if (cookieConsent !== 'true') {
            setOpen(true);
        } else {
            // Load preferences
            const storedPreferences = localStorage.getItem('cookiePreferences');
            if (storedPreferences) {
                try {
                    setPreferences(JSON.parse(storedPreferences));
                } catch (error) {
                    console.error(
                        'Failed to parse stored cookie preferences',
                        error,
                    );
                }
            }

            // Check if the consent version has changed and needs renewal
            const storedVersion = localStorage.getItem('cookieConsentVersion');
            if (storedVersion !== consentVersion) {
                setOpen(true);
            }
        }
    }, [consentVersion]);

    const handleAcceptAll = async () => {
        const allConsent = {
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true,
        };

        await savePreferences(allConsent);
    };

    const handleAcceptNecessary = async () => {
        const necessaryOnly = {
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
        };

        await savePreferences(necessaryOnly);
    };

    const handleSavePreferences = async () => {
        await savePreferences(preferences);
    };

    const savePreferences = async (prefs: CookiePreferences) => {
        try {
            // Save locally first
            localStorage.setItem('cookieConsent', 'true');
            localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
            localStorage.setItem('cookieConsentVersion', consentVersion);

            setPreferences(prefs);
            setOpen(false);

            // Dispatch custom event
            window.dispatchEvent(
                new CustomEvent('cookieConsentUpdated', {
                    detail: prefs,
                }),
            );

            // Send to server using native fetch instead of Inertia
            const response = await fetch('/gdpr/cookie-consent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN':
                        document
                            .querySelector('meta[name="csrf-token"]')
                            ?.getAttribute('content') || '',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    preferences: prefs,
                    version: consentVersion,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to save preferences');
            }

            toast({
                title: 'Cookie preferences saved',
                description: 'Your cookie preferences have been updated.',
                duration: 3000,
            });
        } catch (error) {
            console.error('Error saving cookie preferences:', error);
            toast({
                title: 'Error saving preferences',
                description:
                    'There was an error saving your cookie preferences. Please try again.',
                variant: 'destructive',
                duration: 5000,
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-3xl sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <ShieldCheck className="mr-2 h-5 w-5 text-emerald-500" />
                        Cookie Preferences
                    </DialogTitle>
                    <DialogDescription>
                        We use cookies to enhance your browsing experience,
                        personalize content, and analyze our traffic.
                    </DialogDescription>
                </DialogHeader>

                <Tabs
                    defaultValue="simple"
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="simple">Simple View</TabsTrigger>
                        <TabsTrigger value="detailed">
                            Detailed View
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="simple" className="space-y-4 py-4">
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                We use cookies and similar technologies to
                                provide various features on our website. Choose
                                your consent level below:
                            </p>

                            <div className="flex flex-col space-y-2">
                                <Button
                                    variant="default"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-800"
                                    onClick={handleAcceptAll}
                                >
                                    Accept All Cookies
                                </Button>

                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={handleAcceptNecessary}
                                >
                                    Accept Necessary Only
                                </Button>

                                <Button
                                    variant="ghost"
                                    className="w-full"
                                    onClick={() => setActiveTab('detailed')}
                                >
                                    Customize Settings
                                </Button>
                            </div>

                            <div className="text-xs text-muted-foreground">
                                <p>
                                    By clicking "Accept All Cookies", you agree
                                    to the storing of cookies on your device to
                                    enhance site navigation, analyze site usage,
                                    and assist in our marketing efforts. For
                                    more information, read our{' '}
                                    <a
                                        href={privacyPolicyUrl}
                                        className="text-emerald-600 hover:underline dark:text-emerald-400"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Privacy Policy
                                    </a>
                                    {cookiePolicyUrl && (
                                        <>
                                            {' '}
                                            and{' '}
                                            <a
                                                href={cookiePolicyUrl}
                                                className="text-emerald-600 hover:underline dark:text-emerald-400"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Cookie Policy
                                            </a>
                                        </>
                                    )}
                                    .
                                </p>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="detailed" className="space-y-4 py-4">
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Customize your cookie preferences below.
                                Necessary cookies cannot be disabled as they are
                                required for the website to function properly.
                            </p>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between space-x-2">
                                    <div className="flex flex-col space-y-1">
                                        <Label
                                            htmlFor="necessary"
                                            className="font-medium"
                                        >
                                            Necessary Cookies
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            These cookies are essential for the
                                            website to function properly. Cannot
                                            be disabled.
                                        </p>
                                    </div>
                                    <Switch
                                        id="necessary"
                                        checked={preferences.necessary}
                                        disabled
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between space-x-2">
                                    <div className="flex flex-col space-y-1">
                                        <Label
                                            htmlFor="functional"
                                            className="font-medium"
                                        >
                                            Functional Cookies
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Enable personalized features and
                                            remember your preferences on the
                                            website.
                                        </p>
                                    </div>
                                    <Switch
                                        id="functional"
                                        checked={preferences.functional}
                                        onCheckedChange={(checked) =>
                                            setPreferences((prev) => ({
                                                ...prev,
                                                functional: checked,
                                            }))
                                        }
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between space-x-2">
                                    <div className="flex flex-col space-y-1">
                                        <Label
                                            htmlFor="analytics"
                                            className="font-medium"
                                        >
                                            Analytics Cookies
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Help us understand how visitors
                                            interact with our website and
                                            improve performance.
                                        </p>
                                    </div>
                                    <Switch
                                        id="analytics"
                                        checked={preferences.analytics}
                                        onCheckedChange={(checked) =>
                                            setPreferences((prev) => ({
                                                ...prev,
                                                analytics: checked,
                                            }))
                                        }
                                    />
                                </div>

                                <Separator />

                                <div className="flex items-center justify-between space-x-2">
                                    <div className="flex flex-col space-y-1">
                                        <Label
                                            htmlFor="marketing"
                                            className="font-medium"
                                        >
                                            Marketing Cookies
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Track your activity across websites
                                            to deliver personalized
                                            advertisements.
                                        </p>
                                    </div>
                                    <Switch
                                        id="marketing"
                                        checked={preferences.marketing}
                                        onCheckedChange={(checked) =>
                                            setPreferences((prev) => ({
                                                ...prev,
                                                marketing: checked,
                                            }))
                                        }
                                    />
                                </div>
                            </div>

                            <div className="pt-2">
                                <Button
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-800"
                                    onClick={handleSavePreferences}
                                >
                                    Save Preferences
                                </Button>
                            </div>

                            <div className="text-xs text-muted-foreground">
                                <p>
                                    For more information about cookies and your
                                    privacy, please read our{' '}
                                    <a
                                        href={privacyPolicyUrl}
                                        className="text-emerald-600 hover:underline dark:text-emerald-400"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Privacy Policy
                                    </a>
                                    {cookiePolicyUrl && (
                                        <>
                                            {' '}
                                            and{' '}
                                            <a
                                                href={cookiePolicyUrl}
                                                className="text-emerald-600 hover:underline dark:text-emerald-400"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Cookie Policy
                                            </a>
                                        </>
                                    )}
                                    .
                                </p>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

export default CookieConsent;
